import { CodeBlock, SectionTitle, SubTitle, InfoBox } from './shared';

export default function MemoryContent() {
  return (
    <div>
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-white mb-3">记忆</h1>
        <p className="text-slate-400 text-base leading-relaxed">
          OpenClaw 的记忆系统通过 Markdown 文件和向量搜索，为智能体提供长期知识存储能力。
        </p>
      </div>

      <SectionTitle id="mem-file">记忆文件（Markdown）</SectionTitle>
      <p className="text-slate-300 text-sm leading-relaxed mb-3">
        记忆以 Markdown 文件形式存储在工作区中，智能体可以读写这个文件来积累知识：
      </p>
      <CodeBlock language="markdown" code={`# 记忆文件示例（memory.md）

## 用户偏好
- 喜欢简洁的回答，不喜欢冗长的解释
- 使用中文交流
- 时区：Asia/Shanghai

## 重要信息
- 用户名：张三
- 项目：OpenClaw 文档网站
- 技术栈：React + TypeScript + TailwindCSS

## 待办事项
- [ ] 完成 API 文档
- [x] 设置 Telegram 机器人

## 历史记录
- 2024-01-15：讨论了记忆系统的实现方案
- 2024-01-10：完成了基础配置`} />

      <SectionTitle id="mem-when-write">何时写入记忆</SectionTitle>
      <p className="text-slate-300 text-sm leading-relaxed mb-3">
        智能体在以下情况下会写入记忆：
      </p>
      <ul className="space-y-2 text-sm text-slate-300 mb-4">
        <li className="flex items-start gap-2.5"><i className="ri-pencil-line text-cyan-400 mt-0.5 flex-shrink-0"></i><span><strong className="text-white">用户明确要求：</strong>如"记住我喜欢简洁的回答"</span></li>
        <li className="flex items-start gap-2.5"><i className="ri-pencil-line text-cyan-400 mt-0.5 flex-shrink-0"></i><span><strong className="text-white">压缩前触发：</strong>上下文窗口接近上限时，自动将重要信息写入记忆</span></li>
        <li className="flex items-start gap-2.5"><i className="ri-pencil-line text-cyan-400 mt-0.5 flex-shrink-0"></i><span><strong className="text-white">模型主动判断：</strong>模型认为某些信息值得长期保存时</span></li>
        <li className="flex items-start gap-2.5"><i className="ri-pencil-line text-cyan-400 mt-0.5 flex-shrink-0"></i><span><strong className="text-white">定期刷新：</strong>可配置定期触发记忆整理</span></li>
      </ul>

      <SectionTitle id="mem-auto-refresh">自动记忆刷新（压缩前触发）</SectionTitle>
      <p className="text-slate-300 text-sm leading-relaxed mb-3">
        在触发压缩之前，系统会自动执行记忆刷新流程：
      </p>
      <div className="space-y-3 my-4">
        {[
          '检测到上下文窗口使用率超过阈值（默认 80%）',
          '向模型发送记忆刷新提示：要求将重要信息写入 memory.md',
          '模型调用 write_file 工具更新 memory.md',
          '记忆刷新完成，继续执行压缩',
        ].map((step, i) => (
          <div key={i} className="flex items-start gap-3">
            <span className="w-6 h-6 flex items-center justify-center bg-cyan-500/10 text-cyan-400 rounded-full text-xs font-bold flex-shrink-0 mt-0.5">{i + 1}</span>
            <span className="text-slate-300 text-sm">{step}</span>
          </div>
        ))}
      </div>
      <CodeBlock language="json5" code={`{
  agent: {
    memory: {
      // 触发记忆刷新的上下文使用率阈值
      refreshThreshold: 0.8,
      // 记忆刷新提示词（可自定义）
      refreshPrompt: "请将本次对话中的重要信息整理并写入记忆文件。",
    }
  }
}`} />

      <SectionTitle id="mem-vector">向量记忆搜索</SectionTitle>
      <p className="text-slate-300 text-sm leading-relaxed mb-3">
        OpenClaw 支持向量搜索，可以在大量记忆内容中快速找到相关信息：
      </p>

      <SubTitle id="mem-extra-paths">额外记忆路径</SubTitle>
      <p className="text-slate-300 text-sm leading-relaxed mb-3">
        可以配置多个记忆文件路径，智能体会在所有路径中搜索：
      </p>
      <CodeBlock language="json5" code={`{
  agent: {
    memory: {
      // 主记忆文件
      filePath: "memory.md",
      // 额外的记忆路径（支持 glob 模式）
      extraPaths: [
        "knowledge/*.md",
        "/shared/company-knowledge.md",
      ]
    }
  }
}`} />

      <SubTitle id="mem-gemini">Gemini 嵌入</SubTitle>
      <p className="text-slate-300 text-sm leading-relaxed mb-3">
        使用 Google Gemini 的嵌入模型生成向量：
      </p>
      <CodeBlock language="json5" code={`{
  agent: {
    memory: {
      vectorSearch: {
        enabled: true,
        provider: "gemini",
        model: "text-embedding-004",
        apiKey: "YOUR_GEMINI_API_KEY",
      }
    }
  }
}`} />

      <SubTitle id="mem-hybrid">混合搜索（BM25 + 向量）</SubTitle>
      <p className="text-slate-300 text-sm leading-relaxed mb-3">
        结合 BM25 关键词搜索和向量语义搜索，提高搜索准确性：
      </p>
      <CodeBlock language="json5" code={`{
  agent: {
    memory: {
      vectorSearch: {
        enabled: true,
        // 混合搜索模式
        mode: "hybrid",
        // BM25 权重（0-1）
        bm25Weight: 0.3,
        // 向量搜索权重（0-1）
        vectorWeight: 0.7,
      }
    }
  }
}`} />

      <SubTitle id="mem-embed-cache">嵌入缓存</SubTitle>
      <p className="text-slate-300 text-sm leading-relaxed mb-3">
        嵌入向量会被缓存到本地，避免重复计算：
      </p>
      <CodeBlock language="json5" code={`{
  agent: {
    memory: {
      vectorSearch: {
        cache: {
          enabled: true,
          // 缓存目录
          dir: "~/.openclaw/embed-cache",
          // 缓存过期时间（天）
          expiryDays: 30,
        }
      }
    }
  }
}`} />

      <SubTitle id="mem-session-search">会话记忆搜索</SubTitle>
      <p className="text-slate-300 text-sm leading-relaxed mb-3">
        除了记忆文件，还可以在历史会话中进行向量搜索：
      </p>
      <CodeBlock language="json5" code={`{
  agent: {
    memory: {
      vectorSearch: {
        // 是否在历史会话中搜索
        searchSessions: true,
        // 搜索的最大会话数
        maxSessions: 10,
        // 每个会话返回的最大结果数
        maxResultsPerSession: 3,
      }
    }
  }
}`} />

      <SubTitle id="mem-sqlite">SQLite 向量加速</SubTitle>
      <p className="text-slate-300 text-sm leading-relaxed mb-3">
        使用 SQLite 的向量扩展（sqlite-vec）加速向量搜索：
      </p>
      <CodeBlock language="json5" code={`{
  agent: {
    memory: {
      vectorSearch: {
        // 使用 SQLite 存储向量索引
        backend: "sqlite",
        // SQLite 数据库路径
        dbPath: "~/.openclaw/vectors.db",
      }
    }
  }
}`} />

      <SubTitle id="mem-local-embed">本地嵌入自动下载</SubTitle>
      <p className="text-slate-300 text-sm leading-relaxed mb-3">
        OpenClaw 支持本地嵌入模型，首次使用时自动下载：
      </p>
      <CodeBlock language="json5" code={`{
  agent: {
    memory: {
      vectorSearch: {
        provider: "local",
        // 本地嵌入模型（自动下载）
        model: "nomic-embed-text-v1.5",
        // 模型缓存目录
        modelDir: "~/.openclaw/models",
      }
    }
  }
}`} />
      <InfoBox type="info">
        本地嵌入模型无需 API 密钥，完全离线运行，适合隐私敏感场景。首次下载约需几分钟。
      </InfoBox>

      <SubTitle id="mem-custom-endpoint">自定义 OpenAI 兼容端点</SubTitle>
      <p className="text-slate-300 text-sm leading-relaxed mb-3">
        支持任何兼容 OpenAI 嵌入 API 的服务：
      </p>
      <CodeBlock language="json5" code={`{
  agent: {
    memory: {
      vectorSearch: {
        provider: "openai-compatible",
        baseUrl: "http://localhost:11434/v1",  // Ollama 示例
        model: "nomic-embed-text",
        apiKey: "ollama",  // 占位符
      }
    }
  }
}`} />
    </div>
  );
}
